import { type FormikProps, useFormik } from 'formik';
import React, { useCallback, useEffect, useRef } from 'react';
import * as yup from 'yup';

import { useSettings } from '../../context/SettingsContext';

// TODO: remove duplicate, use common form component for create & update
const parseJsonArray = (value: string | null | undefined): string[] | null => {
   if (!value) return null;
   try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
   } catch {
      return null;
   }
};

const addTranslationValidationSchema = (supportedLanguages: string[]) =>
   yup.object().shape({
      namespace: yup.string().required('This field is required!'),
      name: yup.string().required('This field is required!'),
      ...Object.fromEntries(
         supportedLanguages.map((lang) => [
            lang,
            lang === 'en'
               ? yup.string().required('This field is required!')
               : yup.string().test(
                    'array-match',
                    'Must be a valid JSON array matching English length',
                    function (value) {
                       const enArray = parseJsonArray(this.parent.en);
                       if (!enArray) return true;
                       if (!value) return true;
                       const langArray = parseJsonArray(value);
                       if (!langArray) {
                          return this.createError({
                             message: 'English is an array — this must also be a valid JSON array',
                          });
                       }
                       if (langArray.length !== enArray.length) {
                          return this.createError({
                             message: `Array length must match English (${enArray.length} items)`,
                          });
                       }
                       return true;
                    },
                 ),
         ]),
      ),
   });

export type TranslationFormValues = {
   namespace: string;
   name: string;
} & Record<string, string>;

type Props = {
   onClose: () => void;
   onSave: (values: TranslationFormValues) => Promise<void>;
};
const AddTranslationModal = ({ onClose, onSave }: Props) => {
   const { supportedLanguages } = useSettings();
   const handleBgClose = useCallback(
      (e) => {
         if (e.target.id === 'wrapper') {
            onClose();
         }
      },
      [onClose],
   );

   const {
      values,
      errors,
      touched,
      isSubmitting,
      handleChange,
      handleBlur,
      handleSubmit,
      setFieldValue,
   } = useFormik<TranslationFormValues>({
      initialValues: {
         namespace: '',
         name: '',
         ...Object.fromEntries(
            supportedLanguages.map((lang) => [lang, '']),
         ),
      },
      validationSchema: addTranslationValidationSchema(supportedLanguages),
      onSubmit: async (values) => {
         sessionStorage.setItem(
            'AddTranslationModalLastNamespace',
            values.namespace,
         );
         await onSave(values);
      },
   });

   useEffect(() => {
      const lastNamespace = sessionStorage.getItem(
         'AddTranslationModalLastNamespace',
      );
      if (lastNamespace) {
         setFieldValue('namespace', lastNamespace);
      }
   }, [setFieldValue]);

   return (
      <div
         id='wrapper'
         className='fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-25 backdrop-blur-sm'
         onClick={handleBgClose}
      >
         {/* Header */}
         <div className='flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white px-10 py-5'>
            <div className='flex shrink-0 items-center justify-between'>
               <h1 className='text-xl font-medium'>Add Translation</h1>
               <button
                  className='text-boatpark-black underline'
                  onClick={() => {
                     onClose();
                  }}
               >
                  Close
               </button>
            </div>

            {/* Form Items */}
            <div className='mt-8 flex flex-1 flex-col gap-y-2 overflow-y-auto pr-2'>
               <FormItem
                  required
                  name='namespace'
                  value={values.namespace}
                  error={touched.namespace && errors.namespace}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
               />
               <hr />
               <FormItem
                  required
                  name='name'
                  value={values.name}
                  error={touched.name && errors.name}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
               />
               <hr />
               {supportedLanguages.map((lang) => (
                  <React.Fragment key={lang}>
                     <FormItem
                        name={lang}
                        value={values[lang]}
                        error={touched[lang] && errors[lang]}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        multiline
                     />
                     <hr />
                  </React.Fragment>
               ))}
            </div>

            {/* Action Buttons */}
            <div className='mt-8 flex shrink-0 flex-row justify-end'>
               <button
                  onClick={(e) => {
                     e.preventDefault();
                     onClose();
                  }}
               >
                  <span className='text-red-500 underline'>Cancel</span>
               </button>
               <button
                  type='submit'
                  disabled={isSubmitting}
                  className='ml-4 rounded-md border bg-boatpark-blue px-4 py-1'
                  onClick={() => handleSubmit()}
               >
                  <span className='font-medium text-white'>Save</span>
               </button>
            </div>
         </div>
      </div>
   );
};

type FormItemProps = {
   name: string;
   value: string;
   error?: string;
   required?: boolean;
   multiline?: boolean;
   handleBlur: FormikProps<TranslationFormValues>['handleBlur'];
   handleChange: FormikProps<TranslationFormValues>['handleChange'];
};

const MAX_TEXTAREA_HEIGHT = 240; // px

const FormItem = ({
   name,
   value,
   error,
   required,
   multiline,
   handleBlur,
   handleChange,
}: FormItemProps) => {
   const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

   const adjustTextAreaHeight = useCallback((el: HTMLTextAreaElement) => {
      el.style.height = 'auto';
      const scrollHeight = el.scrollHeight;
      const nextHeight = Math.min(scrollHeight, MAX_TEXTAREA_HEIGHT);
      el.style.height = `${nextHeight}px`;
      el.style.overflowY = scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
   }, []);

   useEffect(() => {
      if (multiline && textAreaRef.current) {
         adjustTextAreaHeight(textAreaRef.current);
      }
   }, [value, multiline, adjustTextAreaHeight]);

   const handleMultilineChange = useCallback<
      FormikProps<TranslationFormValues>['handleChange']
   >(
      (event) => {
         if (multiline) {
            adjustTextAreaHeight(event.target as HTMLTextAreaElement);
         }
         handleChange(event);
      },
      [adjustTextAreaHeight, handleChange, multiline],
   );

   return (
      <div>
         <div className='flex flex-row items-start gap-x-4'>
            <label className='w-[120px] font-medium text-gray-500'>
               {required ? (
                 <>
                   <span className='text-red-500'>*</span>
                   <strong>{`${name}:`}</strong>
                 </>
               ) : (
                 `${name}:`
               )}
            </label>
            {multiline ? (
               <textarea
                  ref={textAreaRef}
                  className='flex-1 rounded border border-black px-4 py-1 text-sm leading-5'
                  id={name}
                  name={name}
                  value={value}
                  onBlur={handleBlur}
                  onChange={handleMultilineChange}
                  rows={1}
               />
            ) : (
               <input
                  className='flex-1 rounded border border-black px-4 py-1'
                  id={name}
                  name={name}
                  value={value}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='text'
               />
            )}
         </div>
         {error ? <span className='text-xs text-red-500'>{error}</span> : null}
      </div>
   );
};

export default AddTranslationModal;
